/**
 * useHomeData — Fetches and polls real API data for the Home page.
 *
 * Flow:
 *  1. Fetch files → getFiles({ pageSize: 100 }) → FileStorageResponseDto[]
 *     Each file contains documents[] (DocumentListItemDto[])
 *  2. Map to HomeSourceFile[] via home-mappers
 *  3. Poll non-final files every 5s via getFileStatusBatch()
 *  4. Derive type/org filter options from data
 *  5. Fetch document detail on-demand for review panel
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FilterOption } from "@/components/home/filter-dropdown";
import { mapDocumentListItem, mapFileToSourceFile } from "@/lib/home-mappers";
import { convertPDFToImages } from "@/lib/pdf-converter";
import { ApiException, classifyAuthError } from "@/services";
import { useDocumentsApi } from "@/services/api/documents-api";
import { useFilesApi } from "@/services/api/files-api";
import { useOrganizationsApi } from "@/services/api/organizations-api";
import { ExceptionCodeEnum } from "@/types/api/common";
import { type CustomFieldDefinitionDto, type DocumentDetailDto, DocumentStatusEnum, type UpdateCustomFieldsRequestDto } from "@/types/api/documents";
import { type FileStatusDto, FileStatusEnum, type FileStorageResponseDto, isFileFinalStatus } from "@/types/api/files";
import type { OrganizationListItemDto } from "@/types/api/organizations";
import type { HomeDocument, HomeDocumentStatus, HomeSourceFile } from "@/types/home";
import { classifyUploadFile, createSanitizedFile } from "@/utils/file-validation";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const POLL_INTERVAL = 5000;
const MAX_POLL_ERRORS = 5;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Determine if a file needs ongoing polling (upload/processing only — NOT export). */
function fileNeedsPolling(file: FileStorageResponseDto): boolean {
    if (file.errorCode) return false;
    const docs = file.documents ?? [];
    if (docs.length > 0) {
        return docs.some((d) => !d.isProcessingEndStateReached && !d.errorCode);
    }
    return !isFileFinalStatus(file.status);
}

/** Determine if a status DTO should stop polling (upload/processing only). */
function shouldStopPolling(dto: FileStatusDto): boolean {
    if (dto.errorCode) return true;
    const docs = dto.documents ?? [];
    if (docs.length > 0) {
        return docs.every((d) => d.isProcessingEndStateReached || !!d.errorCode);
    }
    return isFileFinalStatus(dto.status);
}

/** Create a placeholder HomeSourceFile for optimistic UI during upload. */
function createPlaceholderSourceFile(file: File, tempId: number): HomeSourceFile {
    const placeholderDoc: HomeDocument = {
        id: tempId,
        vendorName: file.name.replace(/\.[^.]+$/, ""),
        amount: 0,
        confidence: 0,
        documentType: "Uploading",
        documentDate: new Date().toISOString(),
        status: "uploading" as HomeDocumentStatus,
        apiStatus: "Pending",
        sourceFileId: tempId,
        fileStorageId: undefined,
        isProcessingEndStateReached: false,
        hasAllRequiredFields: true,
        hasFieldErrors: false,
    };
    return {
        id: tempId,
        fileName: file.name,
        documents: [placeholderDoc],
    };
}

/** Map file-level FileStatusEnum to a HomeDocumentStatus for the placeholder row. */
function mapFileStatusToHomeStatus(fileStatus?: FileStatusEnum): HomeDocumentStatus {
    switch (fileStatus) {
        case FileStatusEnum.OCR:
            return "reading";
        case FileStatusEnum.Classification:
            return "detecting_type";
        default:
            return "processing";
    }
}

/** Map a HomeDocumentStatus to a human-readable label for the documentType sub-text. */
const STATUS_DOCUMENT_TYPE_LABEL: Partial<Record<HomeDocumentStatus, string>> = {
    uploading: "Uploading",
    reading: "Reading",
    detecting_type: "Detecting Type",
    processing: "Processing",
    finding_vendor: "Finding Vendor",
    finding_receipts: "Finding Receipts",
    matching: "Matching",
    queued: "Queued",
    duplicate: "Duplicate",
    error: "Error",
};

/** Create a single synthetic "processing" document when server file has no documents yet. */
function createProcessingPlaceholderDoc(fileStorageId: number, fileName: string, fileStatus?: FileStatusEnum): HomeDocument {
    const baseName = fileName.replace(/\.[^.]+$/, "") || fileName;
    const status = mapFileStatusToHomeStatus(fileStatus);
    return {
        id: -fileStorageId,
        vendorName: baseName,
        amount: 0,
        confidence: 0,
        documentType: STATUS_DOCUMENT_TYPE_LABEL[status] ?? "Processing",
        documentDate: new Date().toISOString(),
        status,
        apiStatus: "Processing",
        sourceFileId: fileStorageId,
        fileStorageId,
        isProcessingEndStateReached: false,
        hasAllRequiredFields: true,
        hasFieldErrors: false,
    };
}

/** Create a single synthetic error/duplicate document when a file fails processing with no documents. */
function createErrorPlaceholderDoc(fileStorageId: number, fileName: string, errorCode: string): HomeDocument {
    const baseName = fileName.replace(/\.[^.]+$/, "") || fileName;
    const isDuplicate = errorCode === ExceptionCodeEnum.BizDuplicateFile || errorCode === ExceptionCodeEnum.BizDuplicateDocument;
    const uiStatus: HomeDocumentStatus = isDuplicate ? "duplicate" : "error";
    return {
        id: -fileStorageId,
        vendorName: baseName,
        amount: 0,
        confidence: 0,
        documentType: isDuplicate ? "Duplicate" : "Error",
        documentDate: new Date().toISOString(),
        status: uiStatus,
        apiStatus: "Error",
        sourceFileId: fileStorageId,
        fileStorageId,
        errorCode,
        isProcessingEndStateReached: true,
        hasAllRequiredFields: true,
        hasFieldErrors: false,
    };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseHomeDataReturn {
    sourceFiles: HomeSourceFile[];
    isLoading: boolean;
    error: string | null;
    typeOptions: FilterOption[];
    orgOptions: FilterOption[];
    /** Raw organization list — scoped to current user by backend (used for org dropdown in review panel) */
    orgData: OrganizationListItemDto[];
    /** Full document detail (fetched on-demand for review panel) */
    documentDetail: DocumentDetailDto | null;
    isDetailLoading: boolean;
    /** Fetch full detail for a document (custom fields, pages, etc.) */
    fetchDocumentDetail: (docId: number) => void;
    /** Re-fetch all files */
    refresh: () => Promise<void>;
    /** Re-fetch all files without showing full-page loading screen */
    silentRefresh: () => Promise<void>;
    /** Resume the file-processing poll interval if it isn't already running (idempotent) */
    startPolling: () => void;
    /** Delete a document via API and refresh */
    deleteDocument: (docId: number) => Promise<void>;
    /** Batch-delete multiple documents via API */
    deleteDocuments: (docIds: number[]) => Promise<void>;
    /** Delete a file (source file) via API — used for processing files with no real documents yet */
    deleteFile: (fileId: number) => Promise<void>;
    /** Export documents via API */
    exportDocuments: (docIds: number[], format: string) => Promise<void>;
    /** Upload files via API (sanitize + convert to images + upload) */
    uploadFiles: (files: File[]) => Promise<void>;
    /** Check if user can upload (quota check) */
    canUpload: () => Promise<boolean>;
    /** Custom field definitions for the currently viewed document type */
    customFieldDefinitions: CustomFieldDefinitionDto[];
    /** LineItem-scoped field definitions for the currently viewed document type's line item types */
    lineItemFieldDefinitions: CustomFieldDefinitionDto[];
    /** Update custom field values on a document */
    updateCustomFields: (docId: number, dto: UpdateCustomFieldsRequestDto) => Promise<void>;
    /** Update the organization assigned to a document */
    updateDocumentOrganization: (docId: number, organizationId: number) => Promise<void>;
    /** Track document IDs for export polling (Queued → Exported/Error) */
    trackExportedDocIds: (docIds: number[]) => void;
    /** True when the user is not assigned to any organization (backend 401 with AuthzNoOrganizationAssigned) */
    hasNoOrganization: boolean;
    /** True when requests fail with an auth-expiry 401 even after a token refresh retry — the session lapsed */
    isSessionExpired: boolean;
}

export function useHomeData(): UseHomeDataReturn {
    const filesApi = useFilesApi();
    const documentsApi = useDocumentsApi();
    const organizationsApi = useOrganizationsApi();

    const [sourceFiles, setSourceFiles] = useState<HomeSourceFile[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Organization options (fetched from API)
    const [orgOptions, setOrgOptions] = useState<FilterOption[]>([]);
    // Raw org list — backend-scoped to current user; used by the review panel dropdown
    const [orgData, setOrgData] = useState<OrganizationListItemDto[]>([]);
    // True when backend rejects requests because user has no organization assignment
    const [hasNoOrganization, setHasNoOrganization] = useState(false);
    // True when the session lapsed (auth-expiry 401 persisting after useApi's silent token-refresh retry)
    const [isSessionExpired, setIsSessionExpired] = useState(false);

    /**
     * Shared 401 triage. Returns true when the error was an auth failure that has been
     * surfaced to the user (no-org dialog or session-expired prompt); callers must not
     * apply their generic error handling in that case. An expired session must never be
     * presented as a missing organization assignment — they are different problems.
     */
    const handleAuthError = useCallback((err: unknown): boolean => {
        const kind = classifyAuthError(err);
        if (kind === "no-organization") {
            setHasNoOrganization(true);
            return true;
        }
        if (kind === "session-expired") {
            setIsSessionExpired(true);
            return true;
        }
        return false;
    }, []);

    // Document detail (for review panel)
    const [documentDetail, setDocumentDetail] = useState<DocumentDetailDto | null>(null);
    const [isDetailLoading, setIsDetailLoading] = useState(false);

    // Custom field definitions (cached per (documentTypeId, organizationId) — defs are org-scoped)
    const [customFieldDefinitions, setCustomFieldDefinitions] = useState<CustomFieldDefinitionDto[]>([]);
    // LineItem-scoped definitions for the same document type (rides along on the same fetch)
    const [lineItemFieldDefinitions, setLineItemFieldDefinitions] = useState<CustomFieldDefinitionDto[]>([]);
    const fieldDefCacheRef = useRef(new Map<string, { document: CustomFieldDefinitionDto[]; lineItem: CustomFieldDefinitionDto[] }>());

    // Polling: single interval for all file IDs (pollIdsRef); one batch request per tick
    const pollIdsRef = useRef(new Set<number>());
    const errorCountsRef = useRef(new Map<number, number>());
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const isPollActiveRef = useRef(false);

    // Keep latest sourceFiles accessible in polling callbacks
    const rawFilesRef = useRef<FileStorageResponseDto[]>([]);

    // Export polling: separate from file processing polling.
    // Tracks document IDs in Queued status (bridge export in progress).
    // Uses getDocumentStatusBatch — one batched call per tick.
    const exportPollIdsRef = useRef(new Set<number>());
    const exportIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const isExportPollActiveRef = useRef(false);

    // Optimistic upload placeholders: map temp IDs (negative) → real fileStorageIds (positive)
    const tempToRealIdRef = useRef(new Map<number, number>());

    // Single set of delayed merge-refresh timeouts (cleared when a new upload batch finishes)
    const delayedRefreshTimeoutsRef = useRef<{ t1: number; t2: number } | null>(null);
    const tempIdCounterRef = useRef(0);
    // Guard for concurrent mergeRefresh calls
    const isMergeActiveRef = useRef(false);

    // -------------------------------------------------------------------------
    // Fetch files
    // -------------------------------------------------------------------------

    const fetchFiles = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await filesApi.getFiles({ pageSize: 100 });
            const active = response.data.filter((f) => f.status !== FileStatusEnum.Deleted);
            rawFilesRef.current = active;
            setSourceFiles(active.map(mapFileToSourceFile));

            // Determine which files need polling
            pollIdsRef.current.clear();
            for (const file of active) {
                if (fileNeedsPolling(file)) {
                    pollIdsRef.current.add(file.id);
                }
            }
        } catch (err) {
            if (!handleAuthError(err)) {
                console.error("Failed to fetch files:", err);
                setError("Failed to load documents");
            }
        } finally {
            setIsLoading(false);
        }
    }, [filesApi, handleAuthError]);

    // -------------------------------------------------------------------------
    // Poll active files
    // -------------------------------------------------------------------------

    const pollFiles = useCallback(async () => {
        if (pollIdsRef.current.size === 0 || isPollActiveRef.current) return;
        isPollActiveRef.current = true;

        const ids = Array.from(pollIdsRef.current);

        try {
            const batch = await filesApi.getFileStatusBatch(ids);
            const stopIds: number[] = [];

            // Process statuses
            for (const dto of batch.statuses) {
                errorCountsRef.current.delete(dto.id);
                if (shouldStopPolling(dto)) stopIds.push(dto.id);
            }

            // Handle not-found
            for (const nfId of batch.notFoundIds) {
                const count = (errorCountsRef.current.get(nfId) ?? 0) + 1;
                errorCountsRef.current.set(nfId, count);
                if (count >= MAX_POLL_ERRORS) stopIds.push(nfId);
            }

            // Remove completed from polling
            for (const id of stopIds) pollIdsRef.current.delete(id);

            // Collect tempIds that get promoted (to delete AFTER setSourceFiles completes)
            const promotedTempIds: number[] = [];

            // Merge updates into sourceFiles (including placeholder-to-real transitions)
            setSourceFiles((prev) => {
                const updated = [...prev];
                for (const dto of batch.statuses) {
                    // Direct ID match (normal case for real server files)
                    let idx = updated.findIndex((sf) => sf.id === dto.id);

                    // Secondary: find placeholder that maps to this real ID
                    if (idx === -1) {
                        for (const [tempId, realId] of tempToRealIdRef.current) {
                            if (realId === dto.id) {
                                idx = updated.findIndex((sf) => sf.id === tempId);
                                if (idx !== -1) {
                                    const docs = (dto.documents ?? [])
                                        .filter((d) => d.status !== DocumentStatusEnum.Deleted)
                                        .map((d) => mapDocumentListItem(d, dto.id));
                                    const fileName = updated[idx].fileName;
                                    // Promote placeholder to real file even when no documents yet
                                    // (server may return file before queue creates documents)
                                    updated[idx] = {
                                        id: dto.id,
                                        fileName,
                                        documents:
                                            docs.length > 0
                                                ? docs
                                                : dto.errorCode
                                                  ? [createErrorPlaceholderDoc(dto.id, fileName, dto.errorCode)]
                                                  : [createProcessingPlaceholderDoc(dto.id, fileName, dto.status)],
                                        organizationId: updated[idx].organizationId,
                                        organizationName: updated[idx].organizationName,
                                    };
                                    // Mark for deletion AFTER setSourceFiles (not inside updater)
                                    promotedTempIds.push(tempId);
                                }
                                break;
                            }
                        }
                        continue;
                    }

                    const mappedDocs = (dto.documents ?? []).filter((d) => d.status !== DocumentStatusEnum.Deleted).map((d) => mapDocumentListItem(d, dto.id));
                    updated[idx] = {
                        ...updated[idx],
                        fileStatus: dto.status,
                        documents:
                            mappedDocs.length > 0
                                ? mappedDocs
                                : dto.errorCode
                                  ? [createErrorPlaceholderDoc(dto.id, updated[idx].fileName, dto.errorCode)]
                                  : [createProcessingPlaceholderDoc(dto.id, updated[idx].fileName, dto.status)],
                    };
                }
                return updated;
            });

            // Clean up promoted placeholders AFTER state update (outside the updater to avoid StrictMode issues)
            for (const tempId of promotedTempIds) {
                tempToRealIdRef.current.delete(tempId);
            }

            // Stop interval if nothing left to poll
            if (pollIdsRef.current.size === 0 && intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        } catch (err) {
            // Auth failures are surfaced globally (session-expired prompt / no-org dialog) and
            // must NOT count against individual files — dropping them from polling would leave
            // documents stuck at "processing" forever with no visible error.
            if (!handleAuthError(err)) {
                console.error("Polling error:", err);
                for (const id of ids) {
                    const count = (errorCountsRef.current.get(id) ?? 0) + 1;
                    errorCountsRef.current.set(id, count);
                    if (count >= MAX_POLL_ERRORS) pollIdsRef.current.delete(id);
                }
            }
        } finally {
            isPollActiveRef.current = false;
        }
    }, [filesApi, handleAuthError]);

    // Stable ref for the interval callback
    const pollRef = useRef(pollFiles);
    pollRef.current = pollFiles;

    // -------------------------------------------------------------------------
    // Start / stop polling
    // -------------------------------------------------------------------------

    const startPolling = useCallback(() => {
        if (intervalRef.current) return;
        pollRef.current();
        intervalRef.current = setInterval(() => pollRef.current(), POLL_INTERVAL);
    }, []);

    // After fetch, start polling if needed
    useEffect(() => {
        if (!isLoading && pollIdsRef.current.size > 0) {
            startPolling();
        }
    }, [isLoading, startPolling]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            if (exportIntervalRef.current) {
                clearInterval(exportIntervalRef.current);
                exportIntervalRef.current = null;
            }
        };
    }, []);

    // -------------------------------------------------------------------------
    // Export polling (separate from file processing polling)
    // -------------------------------------------------------------------------

    const pollExports = useCallback(async () => {
        if (exportPollIdsRef.current.size === 0 || isExportPollActiveRef.current) return;
        isExportPollActiveRef.current = true;

        const docIds = Array.from(exportPollIdsRef.current);

        try {
            const batch = await documentsApi.getDocumentStatusBatch(docIds);
            const doneIds: number[] = [];

            for (const dto of batch.statuses) {
                // Document left Queued → stop polling it. A permanently failed export keeps the
                // document Queued but surfaces an errorCode — that is also terminal for polling
                // (hasRetryWarning suppresses the code while an automatic retry is still pending,
                // so in-flight retries keep polling).
                if (dto.status !== DocumentStatusEnum.Queued || dto.errorCode) {
                    doneIds.push(dto.id);
                }
            }
            // Not-found documents — stop polling
            for (const nfId of batch.notFoundIds) {
                doneIds.push(nfId);
            }

            for (const id of doneIds) exportPollIdsRef.current.delete(id);

            // If any documents changed, refresh the file list to update UI
            if (doneIds.length > 0) {
                const response = await filesApi.getFiles({ pageSize: 100 });
                const active = response.data.filter((f) => f.status !== FileStatusEnum.Deleted);
                rawFilesRef.current = active;
                setSourceFiles(active.map(mapFileToSourceFile));
            }

            // Stop interval if nothing left
            if (exportPollIdsRef.current.size === 0 && exportIntervalRef.current) {
                clearInterval(exportIntervalRef.current);
                exportIntervalRef.current = null;
            }
        } catch (err) {
            if (!handleAuthError(err)) {
                console.error("Export polling error:", err);
            }
        } finally {
            isExportPollActiveRef.current = false;
        }
    }, [documentsApi, filesApi, handleAuthError]);

    const exportPollRef = useRef(pollExports);
    exportPollRef.current = pollExports;

    /** Add document IDs to the export poll set and start polling if not already running. */
    const trackExportedDocIds = useCallback((docIds: number[]) => {
        for (const id of docIds) exportPollIdsRef.current.add(id);
        if (!exportIntervalRef.current) {
            exportPollRef.current();
            exportIntervalRef.current = setInterval(() => exportPollRef.current(), POLL_INTERVAL);
        }
    }, []);

    // -------------------------------------------------------------------------
    // Initial fetch
    // -------------------------------------------------------------------------

    useEffect(() => {
        fetchFiles();
    }, [fetchFiles]);

    // -------------------------------------------------------------------------
    // Fetch organizations (once on mount)
    // -------------------------------------------------------------------------

    const fetchOrganizations = useCallback(async () => {
        try {
            const response = await organizationsApi.getOrganizations(100, 0);
            const active = response.data.filter((org) => org.isActive).sort((a, b) => a.name.localeCompare(b.name));
            setOrgData(active);
            setOrgOptions(active.map((org) => ({ id: String(org.id), label: org.name })));
        } catch (err) {
            if (!handleAuthError(err)) {
                console.error("Failed to fetch organizations:", err);
            }
            setOrgData([]);
            setOrgOptions([]);
        }
    }, [organizationsApi, handleAuthError]);

    useEffect(() => {
        fetchOrganizations();
    }, [fetchOrganizations]);

    // -------------------------------------------------------------------------
    // Document detail (on-demand)
    // -------------------------------------------------------------------------

    const fetchFieldDefinitions = useCallback(
        async (documentTypeId: number, organizationId: number | null) => {
            const cacheKey = `${documentTypeId}:${organizationId ?? "default"}`;
            const cached = fieldDefCacheRef.current.get(cacheKey);
            if (cached) {
                setCustomFieldDefinitions([...cached.document]);
                setLineItemFieldDefinitions([...cached.lineItem]);
                return;
            }
            try {
                const response = await documentsApi.getCustomFieldDefinitions(documentTypeId, organizationId);
                // lineItemCustomFieldDefinitions is absent on older backends — treat as none
                const entry = {
                    document: response.customFieldDefinitions,
                    lineItem: response.lineItemCustomFieldDefinitions ?? [],
                };
                fieldDefCacheRef.current.set(cacheKey, entry);
                setCustomFieldDefinitions(entry.document);
                setLineItemFieldDefinitions(entry.lineItem);
            } catch (err) {
                console.error("Failed to fetch field definitions:", err);
                setCustomFieldDefinitions([]);
                setLineItemFieldDefinitions([]);
            }
        },
        [documentsApi],
    );

    const fetchDocumentDetail = useCallback(
        async (docId: number) => {
            // Skip API call for placeholder/synthetic documents (negative temp IDs or id=0)
            // Clear stale detail so center/right panels don't use data from a previous document
            if (docId <= 0) {
                setDocumentDetail(null);
                setCustomFieldDefinitions([]);
                setLineItemFieldDefinitions([]);
                return;
            }

            setDocumentDetail(null);
            setCustomFieldDefinitions([]);
            setLineItemFieldDefinitions([]);
            setIsDetailLoading(true);
            try {
                const detail = await documentsApi.getDocumentById(docId);
                setDocumentDetail(detail);
                // Fetch field definitions for this document's type AND organization — defs are
                // org-scoped, so a user with multiple orgs must load the definitions for the
                // org that owns the document or value ↔ definition matching silently fails.
                if (detail.documentType?.id) {
                    fetchFieldDefinitions(detail.documentType.id, detail.organizationId ?? null);
                } else {
                    setCustomFieldDefinitions([]);
                    setLineItemFieldDefinitions([]);
                }
            } catch (err) {
                console.error("Failed to fetch document detail:", err);
                setDocumentDetail(null);
                setCustomFieldDefinitions([]);
                setLineItemFieldDefinitions([]);
            } finally {
                setIsDetailLoading(false);
            }
        },
        [documentsApi, fetchFieldDefinitions],
    );

    // -------------------------------------------------------------------------
    // Actions
    // -------------------------------------------------------------------------

    const refresh = useCallback(async () => {
        await fetchFiles();
    }, [fetchFiles]);

    /** Re-fetch files without showing the full-page loading screen. */
    const silentRefresh = useCallback(async () => {
        try {
            const response = await filesApi.getFiles({ pageSize: 100 });
            const active = response.data.filter((f) => f.status !== FileStatusEnum.Deleted);
            rawFilesRef.current = active;
            setSourceFiles(active.map(mapFileToSourceFile));

            pollIdsRef.current.clear();
            for (const file of active) {
                if (fileNeedsPolling(file)) {
                    pollIdsRef.current.add(file.id);
                }
            }
        } catch (err) {
            if (!handleAuthError(err)) {
                console.error("Silent refresh failed:", err);
            }
        }
    }, [filesApi, handleAuthError]);

    /**
     * Placeholder-aware refresh: fetches server data and merges with any
     * optimistic upload placeholders that haven't been replaced yet.
     */
    const mergeRefresh = useCallback(async () => {
        if (isMergeActiveRef.current) return;
        isMergeActiveRef.current = true;
        try {
            const response = await filesApi.getFiles({ pageSize: 100 });
            const active = response.data.filter((f) => f.status !== FileStatusEnum.Deleted);
            rawFilesRef.current = active;

            const serverFileMap = new Map<number, HomeSourceFile>();
            const serverFileByName = new Map<string, HomeSourceFile>();
            for (const f of active) {
                const mapped = mapFileToSourceFile(f);
                serverFileMap.set(f.id, mapped);
                serverFileByName.set(f.fileName, mapped);
            }

            // Collect tempIds to delete AFTER setSourceFiles (avoid StrictMode issues)
            const promotedTempIds: number[] = [];

            setSourceFiles((prev) => {
                const result: HomeSourceFile[] = [];
                const usedServerIds = new Set<number>();
                const placeholderFileNames = new Set<string>();

                // Pass 1: Collect placeholder file names for deduplication.
                // Only include non-errored placeholders — errored/duplicate placeholders must not
                // suppress the original server file from appearing in Pass 3.
                for (const sf of prev) {
                    if (sf.id < 0 && !sf.documents.some((d) => d.status === "duplicate" || d.status === "error")) {
                        placeholderFileNames.add(sf.fileName);
                    }
                }

                // Pass 2: Process existing items
                for (const sf of prev) {
                    if (sf.id >= 0) {
                        // Real file: refresh from server, skip if a placeholder for this file exists
                        if (placeholderFileNames.has(sf.fileName)) continue;
                        if (usedServerIds.has(sf.id)) continue;
                        const fresh = serverFileMap.get(sf.id);
                        if (fresh) {
                            // If server file has no documents yet, add synthetic processing doc
                            if (fresh.documents.length > 0) {
                                result.push(fresh);
                            } else {
                                result.push({
                                    ...fresh,
                                    documents: [createProcessingPlaceholderDoc(fresh.id, fresh.fileName, fresh.fileStatus as FileStatusEnum | undefined)],
                                });
                            }
                        } else {
                            result.push(sf);
                        }
                        usedServerIds.add(sf.id);
                    } else {
                        // Placeholder: replace with server file if fileName matches,
                        // but only if the placeholder hasn't already reached a terminal error/duplicate state.
                        // A duplicate-errored placeholder must not be overwritten by the original (pre-existing)
                        // server file that triggered the duplicate rejection.
                        const placeholderIsErrored = sf.documents.some((d) => d.status === "duplicate" || d.status === "error");
                        const backendFile = !placeholderIsErrored ? serverFileByName.get(sf.fileName) : undefined;
                        if (backendFile) {
                            // If server file has no documents yet, add synthetic processing doc
                            if (backendFile.documents.length > 0) {
                                result.push(backendFile);
                            } else {
                                result.push({
                                    ...backendFile,
                                    documents: [
                                        createProcessingPlaceholderDoc(
                                            backendFile.id,
                                            backendFile.fileName,
                                            backendFile.fileStatus as FileStatusEnum | undefined,
                                        ),
                                    ],
                                });
                            }
                            usedServerIds.add(backendFile.id);
                            promotedTempIds.push(sf.id);
                            continue;
                        }
                        // No match yet — keep placeholder in processing state
                        const realId = tempToRealIdRef.current.get(sf.id);
                        result.push({
                            ...sf,
                            documents: sf.documents.map((d) => ({
                                ...d,
                                status: d.status === "uploading" ? ("processing" as HomeDocumentStatus) : d.status,
                                fileStorageId: realId ?? d.fileStorageId,
                                sourceFileId: realId ?? d.sourceFileId,
                            })),
                        });
                        if (realId != null) usedServerIds.add(realId);
                    }
                }

                // Pass 3: Add new server files not already in list
                for (const f of active) {
                    if (usedServerIds.has(f.id)) continue;
                    if (placeholderFileNames.has(f.fileName)) continue;
                    const mapped = serverFileMap.get(f.id);
                    if (mapped) {
                        // If server file has no documents yet, add synthetic processing doc
                        if (mapped.documents.length > 0) {
                            result.push(mapped);
                        } else {
                            result.push({
                                ...mapped,
                                documents: [createProcessingPlaceholderDoc(mapped.id, mapped.fileName, mapped.fileStatus as FileStatusEnum | undefined)],
                            });
                        }
                    }
                }

                return result;
            });

            // Clean up promoted placeholders AFTER state update (avoid StrictMode double-call issues)
            for (const tempId of promotedTempIds) {
                tempToRealIdRef.current.delete(tempId);
            }

            // Update polling IDs
            pollIdsRef.current.clear();
            for (const file of active) {
                if (fileNeedsPolling(file)) {
                    pollIdsRef.current.add(file.id);
                }
            }
            // Also poll real IDs for active placeholders
            for (const [, realId] of tempToRealIdRef.current) {
                pollIdsRef.current.add(realId);
            }
        } catch (err) {
            if (!handleAuthError(err)) {
                console.error("Merge refresh failed:", err);
            }
        } finally {
            isMergeActiveRef.current = false;
        }
    }, [filesApi, handleAuthError]);

    const deleteDocument = useCallback(
        async (docId: number) => {
            try {
                await documentsApi.deleteDocument(docId);
                if (documentDetail?.id === docId) setDocumentDetail(null);
                await silentRefresh();
            } catch (err) {
                console.error("Failed to delete document:", err);
                await refresh();
            }
        },
        [documentsApi, documentDetail, silentRefresh, refresh],
    );

    const deleteDocuments = useCallback(
        async (docIds: number[]) => {
            if (docIds.length === 0) return;
            try {
                await documentsApi.deleteDocuments(docIds);
                if (documentDetail && new Set(docIds).has(documentDetail.id)) setDocumentDetail(null);
                await silentRefresh();
            } catch (err) {
                console.error("Failed to batch-delete documents:", err);
                await refresh();
            }
        },
        [documentsApi, documentDetail, silentRefresh, refresh],
    );

    const deleteFile = useCallback(
        async (fileId: number) => {
            try {
                await filesApi.deleteFile(fileId);
                setDocumentDetail(null);
                await silentRefresh();
            } catch (err) {
                console.error("Failed to delete file:", err);
                await refresh();
            }
        },
        [filesApi, silentRefresh, refresh],
    );

    const updateCustomFields = useCallback(
        async (docId: number, dto: UpdateCustomFieldsRequestDto) => {
            await documentsApi.updateCustomFields(docId, dto);
            // Re-fetch detail to get updated values
            const detail = await documentsApi.getDocumentById(docId);
            setDocumentDetail(detail);
        },
        [documentsApi],
    );

    const updateDocumentOrganization = useCallback(
        async (docId: number, organizationId: number) => {
            await documentsApi.updateDocumentOrganization(docId, organizationId);
            // Re-fetch detail so organizationId/organizationName reflect the change
            const detail = await documentsApi.getDocumentById(docId);
            setDocumentDetail(detail);
        },
        [documentsApi],
    );

    const exportDocuments = useCallback(
        async (docIds: number[], format: string) => {
            try {
                await documentsApi.exportDocuments(docIds, format as "csv" | "external");
                await silentRefresh();
            } catch (err) {
                console.error("Failed to export documents:", err);
            }
        },
        [documentsApi, silentRefresh],
    );

    // -------------------------------------------------------------------------
    // Upload
    // -------------------------------------------------------------------------

    const uploadFiles = useCallback(
        async (files: File[], organizationId?: number) => {
            // 1. Create optimistic placeholders and add to state IMMEDIATELY
            const entries: Array<{ tempId: number; file: File }> = [];
            for (const rawFile of files) {
                const file = createSanitizedFile(rawFile);
                tempIdCounterRef.current -= 1;
                const tempId = tempIdCounterRef.current;
                entries.push({ tempId, file });
            }

            const placeholders = entries.map(({ tempId, file }) => createPlaceholderSourceFile(file, tempId));
            setSourceFiles((prev) => [...placeholders, ...prev]);

            // 2. Fire-and-forget: upload work runs in the background so the
            //    caller (UploadModal) returns immediately and the user sees
            //    the animated "Uploading" spinners in the left panel.
            void (async () => {
                for (const { tempId, file } of entries) {
                    try {
                        // Office files upload natively: no page images, pageCount = 1.
                        // PDFs convert pages to images for OCR exactly as before.
                        const classification = classifyUploadFile(file);
                        const isOffice = classification.accepted && classification.isOffice;

                        let images: File[] = [];
                        if (!isOffice && file.type === "application/pdf") {
                            try {
                                const pdfUrl = URL.createObjectURL(file);
                                try {
                                    images = await convertPDFToImages(pdfUrl, file);
                                } finally {
                                    URL.revokeObjectURL(pdfUrl);
                                }
                            } catch (err) {
                                console.warn("Failed to convert PDF to images, uploading without:", err);
                            }
                        }

                        const pageCount = isOffice ? 1 : images.length || undefined;
                        const jobResponse = await filesApi.uploadFile(file, images, pageCount, organizationId);
                        const realId = jobResponse.fileStorageId;

                        // Record mapping for polling/refresh reconciliation
                        tempToRealIdRef.current.set(tempId, realId);

                        // Transition placeholder: uploading → processing (keeps spinner visible)
                        setSourceFiles((prev) =>
                            prev.map((sf) => {
                                if (sf.id !== tempId) return sf;
                                return {
                                    ...sf,
                                    documents: sf.documents.map((d) => ({
                                        ...d,
                                        status: "processing" as HomeDocumentStatus,
                                        fileStorageId: realId,
                                        sourceFileId: realId,
                                    })),
                                };
                            }),
                        );

                        // Add uploaded file to polling
                        pollIdsRef.current.add(realId);
                        if (!intervalRef.current) startPolling();
                    } catch (err) {
                        console.error(`Failed to upload ${file.name}:`, err);

                        const errorCode = err instanceof ApiException && err.code ? err.code : "Unknown";
                        const isDuplicate = errorCode === ExceptionCodeEnum.BizDuplicateFile || errorCode === ExceptionCodeEnum.BizDuplicateDocument;
                        const duplicateFileId = errorCode === ExceptionCodeEnum.BizDuplicateFile && err instanceof ApiException ? err.entityId : undefined;
                        const errorStatus: HomeDocumentStatus = isDuplicate ? "duplicate" : "error";

                        // Transition placeholder to error/duplicate state
                        setSourceFiles((prev) =>
                            prev.map((sf) => {
                                if (sf.id !== tempId) return sf;
                                return {
                                    ...sf,
                                    documents: sf.documents.map((d) => ({
                                        ...d,
                                        status: errorStatus,
                                        documentType: isDuplicate ? "Duplicate" : "Error",
                                        errorCode,
                                        duplicateFileId,
                                    })),
                                };
                            }),
                        );
                    }
                }

                // 3. Merge-aware refresh preserves placeholders that haven't been replaced
                await mergeRefresh();

                // 4. One set of delayed refreshes (shared across all upload batches)
                //    Clear any existing so we don't stack timeouts when user uploads again shortly after
                const prev = delayedRefreshTimeoutsRef.current;
                if (prev) {
                    window.clearTimeout(prev.t1);
                    window.clearTimeout(prev.t2);
                    delayedRefreshTimeoutsRef.current = null;
                }
                delayedRefreshTimeoutsRef.current = {
                    t1: window.setTimeout(() => void mergeRefresh(), 3000),
                    t2: window.setTimeout(() => void mergeRefresh(), 8000),
                };
            })();
        },
        [filesApi, startPolling, mergeRefresh],
    );

    const canUpload = useCallback(async () => {
        try {
            const result = await filesApi.canUpload();
            return result.canUpload;
        } catch {
            return false;
        }
    }, [filesApi]);

    // -------------------------------------------------------------------------
    // Derived filter options
    // -------------------------------------------------------------------------

    const typeOptions: FilterOption[] = useMemo(() => {
        const types = new Set<string>();
        for (const sf of sourceFiles) {
            for (const doc of sf.documents) {
                if (doc.documentType) types.add(doc.documentType);
            }
        }
        return Array.from(types)
            .sort()
            .map((t) => ({ id: t, label: t }));
    }, [sourceFiles]);

    // -------------------------------------------------------------------------
    // Return
    // -------------------------------------------------------------------------

    return useMemo(
        () => ({
            sourceFiles,
            isLoading,
            error,
            typeOptions,
            orgOptions,
            orgData,
            documentDetail,
            isDetailLoading,
            fetchDocumentDetail,
            refresh,
            silentRefresh,
            startPolling,
            deleteDocument,
            deleteDocuments,
            deleteFile,
            exportDocuments,
            uploadFiles,
            canUpload,
            customFieldDefinitions,
            lineItemFieldDefinitions,
            updateCustomFields,
            updateDocumentOrganization,
            trackExportedDocIds,
            hasNoOrganization,
            isSessionExpired,
        }),
        [
            sourceFiles,
            isLoading,
            error,
            typeOptions,
            orgOptions,
            orgData,
            documentDetail,
            isDetailLoading,
            fetchDocumentDetail,
            refresh,
            silentRefresh,
            startPolling,
            deleteDocument,
            deleteDocuments,
            deleteFile,
            exportDocuments,
            uploadFiles,
            canUpload,
            customFieldDefinitions,
            lineItemFieldDefinitions,
            updateCustomFields,
            updateDocumentOrganization,
            trackExportedDocIds,
            hasNoOrganization,
            isSessionExpired,
        ],
    );
}
